import React from "react";
import {
  Box,
  Fade,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import PolicyRoundedIcon from "@mui/icons-material/PolicyRounded";

const sectionTitleSx = {
  fontWeight: 900,
  fontSize: "1rem",
  color: "rgba(255,255,255,0.94)",
  mb: 0.45,
};

const sectionSubSx = {
  fontSize: "0.84rem",
  color: "rgba(255,255,255,0.5)",
  mb: 2,
  lineHeight: 1.6,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "rgba(255,255,255,0.03)",
    transition: "all 0.2s ease-in-out",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.05)",
      "& fieldset": { borderColor: "rgba(0,225,255,0.55)" },
    },
  },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.58)" },
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": { color: "#fff" },
};

const blockSx = {
  p: { xs: 1.4, md: 1.8 },
  borderRadius: 2,
  border: "1px solid rgba(255,255,255,0.08)",
  bgcolor: "rgba(255,255,255,0.025)",
};

function SectionIntro({ icon, title, subtitle }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(0,225,255,0.12)",
            color: "#8ef4ff",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          <Typography sx={sectionSubSx}>{subtitle}</Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export default function RegrasStep({ state, actions, errors }) {
  return (
    <Stack spacing={2}>
      <Box sx={blockSx}>
        <SectionIntro
          icon={<GavelRoundedIcon sx={{ fontSize: 20 }} />}
          title="Classificação etária"
          subtitle="Defina se menores entram no evento e descreva as condições quando necessário."
        />

        <Paper
          elevation={0}
          sx={{
            p: 1.6,
            mb: 1.6,
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.1)",
            bgcolor: "rgba(255,255,255,0.03)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, color: "rgba(255,255,255,0.92)" }}>
              Permite menores de idade?
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,0.5)" }}>
              Habilite se menores de 18 anos podem entrar, mesmo com condições específicas.
            </Typography>
          </Box>
          <Switch
            checked={state.regras.permiteMenores}
            onChange={(e) => actions.set("regras.permiteMenores", e.target.checked)}
            color="primary"
          />
        </Paper>

        <Fade in={state.regras.permiteMenores} unmountOnExit>
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Regras para menores"
              placeholder="Ex: Menores de 16 anos somente acompanhados dos pais ou responsáveis legais..."
              value={state.regras.detalhesMenores}
              onChange={(e) => actions.set("regras.detalhesMenores", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Fade>
      </Box>

      <Box sx={blockSx}>
        <SectionIntro
          icon={<PolicyRoundedIcon sx={{ fontSize: 20 }} />}
          title="Políticas e termos"
          subtitle="Configure reembolso e os avisos gerais que aparecerão na página pública do evento."
        />

        <Grid container spacing={1.6}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Política de reembolso"
              value={state.regras.politicaReembolso}
              onChange={(e) => actions.set("regras.politicaReembolso", e.target.value)}
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="padrao">Padrão ONURB (Recomendado)</MenuItem>
              <MenuItem value="personalizada">Personalizada</MenuItem>
            </TextField>
          </Grid>

          {state.regras.politicaReembolso === "personalizada" && (
            <Grid item xs={12}>
              <Fade in>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Descreva sua política de reembolso"
                  placeholder="Ex: Cancelamentos aceitos até 48h antes do evento..."
                  value={state.regras.reembolsoPersonalizado}
                  onChange={(e) => actions.set("regras.reembolsoPersonalizado", e.target.value)}
                  error={!!errors["regras.reembolsoPersonalizado"]}
                  helperText={errors["regras.reembolsoPersonalizado"]}
                  sx={inputSx}
                  InputLabelProps={{ shrink: true }}
                />
              </Fade>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={7}
              label="Termos e avisos gerais"
              placeholder="Regras sobre objetos proibidos, filmagem, direito de imagem, etc."
              value={state.regras.avisoImportante}
              onChange={(e) => actions.set("regras.avisoImportante", e.target.value)}
              helperText="Esse texto aparece no rodapé da página do evento."
              sx={inputSx}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}
